import type { Environment, InstallationDetail, RunDetail, TimelineEvent } from '@growth/client';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { PageHeader } from '../components/Layout.tsx';
import { Card, DataTable, download, ErrorBox, JsonView, StatusBadge } from '../components/ui.tsx';
import { ENV_LABEL } from '../filters.ts';
import { fmtDateTime, fmtDuration, fmtInt, fmtNum, shortId } from '../format.ts';
import { invalidateCache, useAction, useApi, useSession } from '../session.tsx';
import { resultLabel } from './Runs.tsx';

function useEnv(): Environment {
  const [sp] = useSearchParams();
  const e = sp.get('environment');
  return e === 'development' || e === 'staging' ? e : 'production';
}

/** Short human summary of an event's params for timelines. */
export function paramSummary(params: Record<string, unknown> | null | undefined, max = 4): string {
  if (!params) return '';
  return Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .slice(0, max)
    .map(([k, v]) => `${k}=${typeof v === 'number' ? fmtNum(v, 2) : String(v)}`)
    .join(' · ');
}

function Timeline({ events, selected, onSelect }: { events: TimelineEvent[]; selected: number | null; onSelect: (i: number) => void }) {
  return (
    <ol className="timeline">
      {events.map((e, i) => (
        <li key={String(e.id)} className={selected === i ? 'selected' : undefined} onClick={() => onSelect(i)} tabIndex={0} onKeyDown={(k) => k.key === 'Enter' && onSelect(i)}>
          <span className="t">{e.t_s === null ? '—' : `${fmtNum(e.t_s, 1)} s`}</span>
          <span className="ellipsis">
            <strong>{e.event_name}</strong> {e.schema_status !== 'valid' && <StatusBadge status={e.schema_status === 'invalid' ? 'error' : 'warn'} />}
          </span>
          <span className="ellipsis muted">{paramSummary(e.params)}</span>
        </li>
      ))}
    </ol>
  );
}

export function RunDetailPage() {
  const { gameId = '', runId = '' } = useParams();
  const env = useEnv();
  const res = useApi<RunDetail>(`run:${gameId}:${env}:${runId}`, (api, s) => api.run(gameId, runId, env, s));
  const [sel, setSel] = useState<number | null>(null);
  const run = res.data?.run;
  const timeline = res.data?.timeline ?? [];
  const q = env === 'production' ? '' : `?environment=${env}`;

  return (
    <>
      <PageHeader
        title={run ? `Run #${run.run_number ?? '?'} · ${resultLabel(run.result)}` : 'Run'}
        sub={
          <>
            <Link to={`/g/${gameId}/runs${q}`}>← Runs</Link> · <span className="mono">{runId}</span> · {ENV_LABEL[env]}
          </>
        }
      />
      {res.error && <ErrorBox error={res.error} onRetry={res.reload} />}
      {run && (
        <div className="split">
          <div className="stack">
            <Card title="Reconstrucción" subtitle={`${timeline.length} eventos ordenados por seq · t = segundos desde run_start`}>
              <Timeline events={timeline} selected={sel} onSelect={setSel} />
            </Card>
          </div>
          <div className="stack sticky">
            <Card title="Resumen de la run">
              <dl className="kv">
                <dt>Jugador</dt>
                <dd>
                  <Link className="mono" to={`/g/${gameId}/players/${run.installation_id}${q}`}>
                    {shortId(run.installation_id, 13)}
                  </Link>
                </dd>
                <dt>Modo / nivel</dt>
                <dd>{[run.mode, run.level_id].filter(Boolean).join(' / ') || '—'}</dd>
                <dt>Personaje</dt>
                <dd>{run.character ?? '—'}</dd>
                <dt>Inicio</dt>
                <dd>{fmtDateTime(run.started_at)}</dd>
                <dt>Duración</dt>
                <dd>{fmtDuration(run.duration_s)}</dd>
                <dt>Oleada</dt>
                <dd>{fmtInt(run.wave_reached)}</dd>
                <dt>Enemigos</dt>
                <dd>{fmtInt(run.enemies_killed)}</dd>
                <dt>Muertes / revives</dt>
                <dd>
                  {fmtInt(run.deaths)} / {fmtInt(run.revives)} ({fmtInt(run.rewarded_revives)} con rewarded)
                </dd>
                <dt>Causa de muerte</dt>
                <dd>{run.death_reason ?? '—'}</dd>
                <dt>Jefe</dt>
                <dd>{run.boss_killed ? 'derrotado' : run.boss_reached ? 'alcanzado' : 'no'}</dd>
                <dt>Anuncios</dt>
                <dd>{fmtInt(run.ads_watched)}</dd>
                <dt>Armas</dt>
                <dd>{run.weapons.join(', ') || '—'}</dd>
                <dt>Mejoras</dt>
                <dd>{run.upgrades.join(', ') || '—'}</dd>
                <dt>Versión / plataforma</dt>
                <dd>
                  {run.app_version ?? '—'} · {run.platform ?? '—'} · {run.country ?? '—'}
                </dd>
                <dt>Adquisición</dt>
                <dd>{[run.acquisition_network, run.campaign_id, run.creative_id].filter(Boolean).join(' / ') || 'orgánico o sin atribuir'}</dd>
                <dt>Integridad</dt>
                <dd>
                  {run.has_start ? 'run_start ✓' : 'sin run_start'} · {run.has_end ? 'run_end ✓' : 'sin run_end'}
                </dd>
              </dl>
            </Card>
            <Card title={sel !== null ? `Evento: ${timeline[sel]?.event_name}` : 'Estadísticas de run_end'}>
              <JsonView value={sel !== null ? timeline[sel] : run.stats} />
            </Card>
          </div>
        </div>
      )}
    </>
  );
}

export function PlayerPage() {
  const { gameId = '', installationId = '' } = useParams();
  const env = useEnv();
  const { api, isAdmin } = useSession();
  const res = useApi<InstallationDetail>(`player:${gameId}:${env}:${installationId}`, (a, s) => a.installation(gameId, installationId, env, s));
  const action = useAction();
  const [deleted, setDeleted] = useState<string | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const d = res.data;
  const inst = d?.installation;
  const q = env === 'production' ? '' : `?environment=${env}`;

  const erase = async () => {
    if (!window.confirm(`Borrar TODOS los datos de ${installationId} en todos los entornos? No se puede deshacer.`)) return;
    const r = await action.run(() => api.deleteInstallation(gameId, installationId));
    if (r) {
      invalidateCache(`player:${gameId}`);
      setDeleted(`Borrado: ${Object.entries(r.rows_deleted).map(([k, v]) => `${k} ${v}`).join(', ')}`);
    }
  };

  return (
    <>
      <PageHeader
        title="Jugador"
        sub={
          <>
            <span className="mono">{installationId}</span> · {ENV_LABEL[env]} · ID seudónimo (installation_id), sin datos personales
          </>
        }
        right={
          isAdmin && (
            <>
              <button type="button" className="btn small" disabled={action.busy} onClick={async () => {
                const r = await action.run(() => api.exportInstallation(gameId, installationId));
                if (r) download(`${gameId}-${installationId}.json`, r);
              }}>
                Exportar datos (RGPD)
              </button>
              <button type="button" className="btn small danger" disabled={action.busy} onClick={erase}>
                Borrar datos (RGPD)
              </button>
            </>
          )
        }
      />
      {action.error && <ErrorBox error={action.error} />}
      {deleted && <div className="notice">{deleted}</div>}
      {res.error && !deleted && <ErrorBox error={res.error} onRetry={res.reload} />}
      {inst && d && (
        <>
          <div className="grid cols-2">
            <Card title="Instalación">
              <dl className="kv">
                <dt>Instalado</dt>
                <dd>
                  {inst.install_date} {inst.is_existing_install ? '(ya instalado antes del SDK: fuera de cohortes)' : ''}
                </dd>
                <dt>Plataforma / país</dt>
                <dd>
                  {inst.platform} · {inst.country ?? '—'}
                </dd>
                <dt>Versiones</dt>
                <dd>
                  {inst.first_app_version ?? '—'} → {inst.last_app_version ?? '—'}
                </dd>
                <dt>Adquisición</dt>
                <dd>{[inst.acquisition_network, inst.campaign_id, inst.creative_id].filter(Boolean).join(' / ') || 'orgánico o sin atribuir'}</dd>
                <dt>Interno</dt>
                <dd>{inst.is_internal ? 'sí (excluido de KPIs)' : 'no'}</dd>
                <dt>Actividad</dt>
                <dd>
                  {fmtInt(inst.active_days)} días · {fmtInt(inst.sessions)} sesiones · {fmtDuration(inst.playtime_s)} · {fmtInt(inst.runs_started)} runs
                </dd>
                <dt>Último día activo</dt>
                <dd>{inst.last_active_date ?? '—'}</dd>
              </dl>
            </Card>
            <Card title="Experimentos">
              {d.experiments.length ? (
                <DataTable
                  data={{
                    columns: [{ label: 'Experimento' }, { label: 'Variante' }, { label: 'Primera exposición' }],
                    rows: d.experiments.map((x) => [x.experiment_key, x.variant_key, fmtDateTime(x.first_seen_at, { seconds: false })]),
                  }}
                />
              ) : (
                <div className="empty">Sin exposiciones.</div>
              )}
            </Card>
          </div>
          <div className="grid cols-2">
            <Card title="Sesiones">
              <DataTable
                data={{
                  columns: [{ label: '#' }, { label: 'Inicio' }, { label: 'Duración', numeric: true }, { label: 'Runs', numeric: true }, { label: 'Fin' }],
                  rows: d.sessions.map((s) => [s.session_number ?? '—', fmtDateTime(s.started_at, { seconds: false }), fmtDuration(s.duration_s), fmtInt(s.run_count), s.end_reason ?? (s.ended_at ? '—' : 'abierta')]),
                }}
              />
            </Card>
            <Card title="Runs">
              <DataTable
                data={{
                  columns: [{ label: '#' }, { label: 'Inicio' }, { label: 'Resultado' }, { label: 'Duración', numeric: true }, { label: 'Oleada', numeric: true }],
                  rows: d.runs.map((r) => [
                    <Link key="r" to={`/g/${gameId}/runs/${r.run_id}${q}`}>
                      #{r.run_number ?? '?'}
                    </Link>,
                    fmtDateTime(r.started_at, { seconds: false }),
                    resultLabel(r.result),
                    fmtDuration(r.duration_s),
                    fmtInt(r.wave_reached),
                  ]),
                }}
              />
            </Card>
          </div>
          <div className="split">
            <Card title="Eventos recientes" subtitle="Últimos 200">
              <DataTable
                compact
                selected={sel}
                onRowClick={setSel}
                data={{
                  columns: [{ label: 'Hora' }, { label: 'Evento' }, { label: 'Estado' }, { label: 'Parámetros' }],
                  rows: d.recent_events.map((e) => [
                    fmtDateTime(e.event_ts),
                    e.event_name,
                    e.schema_status === 'valid' ? 'válido' : <StatusBadge key="s" status={e.schema_status === 'invalid' ? 'error' : 'warn'} />,
                    <span key="p" className="muted small">
                      {paramSummary(e.params, 3)}
                    </span>,
                  ]),
                }}
              />
            </Card>
            <div className="sticky">
              <Card title="Detalle">{sel !== null && d.recent_events[sel] ? <JsonView value={d.recent_events[sel]} /> : <div className="empty">Selecciona un evento.</div>}</Card>
            </div>
          </div>
        </>
      )}
    </>
  );
}
